// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { AgGridUtils } from '@cosmotech/core';
import { Api } from '../../../services/config/Api';
// import { TWINGRAPH_QUERIES_DELAY } from '../../../services/config/FunctionalConstants';
import WorkspaceService from '../../../services/workspace/WorkspaceService';
import { DatasetsUtils } from '../../../utils';
// import { WizardUtils } from '../../../utils/WizardUtils';
import { WIZARD_TWINGRAPH_QUERIES } from '../WizardConstants';

const getOrganizationId = (state) => state.organization.current?.data?.id;
const getWorkspaceId = (state) => state.workspace.current?.data?.id;
const getSolutionData = (state) => state.solution.current?.data;
const getDatasets = (state) => state.dataset.list?.data;

function* getOneQuery(datasetId, scenarioId, query, key) {
  const organizationId = yield select(getOrganizationId);
  try {
    const { data } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, { query });
    return data.map((r) => r.n.properties);
  } catch (e) {
    console.error(e);
  }
}

function* downloadFileData(scenarioId, datasetPartId, key) {
  const organizationId = yield select(getOrganizationId);
  const workspaceId = yield select(getWorkspaceId);
  const datasets = yield select(getDatasets);
  const dataset = datasets?.find((dataset) => dataset.id === datasetPartId);
  if (!dataset) {
    console.warn(`Dataset ${datasetPartId} not found in the dataset list`);
    return;
  }
  const storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
  const data = yield call(WorkspaceService.downloadWorkspaceFileData, organizationId, workspaceId, storageFilePath);
  const fileContentFirstLine = data.split('\n')[0].split(',');
  const columns = fileContentFirstLine.map((col) => ({ field: col, acceptsEmptyFields: true }));
  const parsedData = AgGridUtils.fromCSV(data, true, columns);
  return parsedData.rows;
}

function* getAgingLawsTwingraphQueriesResult(action) {
  const organizationId = yield select(getOrganizationId);
  const solutionData = yield select(getSolutionData);
  const { datasetId, scenarioData } = action.payload;
  const agingLawsParameter = solutionData.parameters?.find(
    (parameter) => parameter.options?.subType === 'AGINGLAWS'
  ).id;
  const scenarioParameters = scenarioData.parametersValues;
  const agingLawsParameterValue = scenarioParameters.find(
    (parameter) => parameter.parameterId === agingLawsParameter
  )?.value;
  const parsedAgingLawParameterValue = agingLawsParameterValue ? JSON.parse(agingLawsParameterValue) : [];
  const scenarioParametersKeys = parsedAgingLawParameterValue?.map((parameter) => parameter.parameterId) ?? [];

  const scenarioId = scenarioData.id;
  try {
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.SET_TWINGRAPH_QUERIES_STATUS,
      status: 'LOADING',
    });

    // Fetch FamilleEquipement data from Twingraph or from AzureStorage
    let familleEquipement;
    if (scenarioParametersKeys.includes('FamilleEquipement')) {
      const parameterValue = parsedAgingLawParameterValue.find(
        (parameter) => parameter.parameterId === 'FamilleEquipement'
      ).value;
      familleEquipement = yield call(downloadFileData, scenarioId, parameterValue, 'FamilleEquipement');
    } else {
      familleEquipement = yield call(
        getOneQuery,
        datasetId,
        scenarioId,
        'MATCH (n: FamilleEquipement) RETURN n',
        'FamilleEquipement'
      );
    }

    // For each FamilleEquipement, fetch the AgeDistribution data from Twingraph
    const familiesAgeDistributionData = [];
    for (const familyProps of familleEquipement) {
      const familyId = familyProps?.Identifiant;
      let ageDistribution = [];

      const ageDistributionLabel = `${familyId}_AgeDistribution`;
      const ageDistributionQuery = `MATCH (n:\`${ageDistributionLabel}\`) RETURN n`;
      try {
        const { data: rawAgeData } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, {
          query: ageDistributionQuery,
        });
        ageDistribution = (rawAgeData || []).map((r) => r.n.properties);
      } catch (e) {
        console.error(`Failed to fetch age distribution for family ${familyId}:`, e);
      }
      familiesAgeDistributionData.push({
        Identifiant: familyId,
        AgeDistribution: ageDistribution,
      });
    }

    // Store the combined FamilleEquipement data (with AgeDistribution)
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.PROCESS_AGING_LAWS_TWINGRAPH_QUERIES,
      payload: { scenarioId, result: familleEquipement, key: 'FamilleEquipement' },
    });
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.PROCESS_AGING_LAWS_TWINGRAPH_QUERIES,
      payload: { scenarioId, result: familiesAgeDistributionData, key: 'AgeDistribution' },
    });

    const { data: niveauHierarchique } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, {
      query: 'MATCH (n: HierarchieDesEquipements) RETURN n',
    });

    const hierarchieEquipements = niveauHierarchique.map((nh) => nh.n.properties);
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.PROCESS_AGING_LAWS_TWINGRAPH_QUERIES,
      payload: { scenarioId, result: hierarchieEquipements, key: 'HierarchieEquipements' },
    });

    // TODO: remove commented code if no longer relevant after refactoring
    // const equipments = hierarchieEquipements.map((nh) => nh.NiveauHierarchique);
    // const queries = [];
    // for (const equipment of equipments) {
    //   if (scenarioParametersKeys.includes(equipment)) {
    //     const parameterValue = parsedAgingLawParameterValue.find(
    //       (parameter) => parameter.parameterId === equipment
    //     ).value;
    //     yield call(downloadFileData, scenarioId, parameterValue, equipment);
    //   } else {
    //     queries.push({ query: WizardUtils.buildQueryString(equipment), key: equipment });
    //   }
    // }
    // yield all(
    //   queries.map((query, index) => {
    //     if (index !== 0) delay(TWINGRAPH_QUERIES_DELAY);
    //     return spawn(getOneQuery, datasetId, scenarioId, query.query, query.key);
    //   })
    // );

    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.SET_TWINGRAPH_QUERIES_STATUS,
      status: 'IDLE',
    });
  } catch (e) {
    console.error(e);
  }
}

function* agingLawsTwingraphQueriesResultSaga() {
  yield takeEvery(
    [WIZARD_TWINGRAPH_QUERIES.INITIALIZE_AGING_LAWS_TWINGRAPH_QUERIES],
    getAgingLawsTwingraphQueriesResult
  );
}

export default agingLawsTwingraphQueriesResultSaga;
